import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  forwardRef,
  inject,
  Input,
  OnDestroy,
  OnInit,
  ViewChild,
} from '@angular/core';
import {
  ControlValueAccessor,
  UntypedFormControl,
  NG_VALUE_ACCESSOR,
  AbstractControl,
  FormControlStatus,
} from '@angular/forms';
import { CustomFormData, FormFields, MonitoringService } from '@orderform/widgets';
import { InputsGeneratorComponent } from '../../../inputs-generator/inputs-generator.component';
import * as PaymentValidators from '../../../../misc/payment-validators';
import { OrderformFascade } from '../../../../fascades/orderform.fascade';
import { debounceTime, distinctUntilChanged, filter, map, skip, takeUntil, tap } from 'rxjs/operators';
import { BehaviorSubject, firstValueFrom, Subject, Subscription } from 'rxjs';
import { getInvalidFieldListFromFormGroup } from '../../../../misc/get-invalid-field-list-from-form-group';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

export const CopyNameOfAddressToAccountHolderDelay = 300;
export const ADDRESS_KEYS_TO_TRY = ['buyer_address_street', 'address_street', 'street', 'buyer_address_private_street'];

@Component({
  selector: 'orderform-payment-method-elv',
  templateUrl: './elv.component.html',
  styleUrls: ['./elv.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => ElvComponent),
      multi: true,
    },
  ],
})
export class ElvComponent implements OnInit, ControlValueAccessor, OnDestroy {
  @Input({ required: true }) config: {
    prefilled: { [fieldname: string]: string };
    engine: string;
  };
  @ViewChild(InputsGeneratorComponent)
  formFieldComponent: InputsGeneratorComponent;

  public ibanFieldList$ = new BehaviorSubject<FormFields.Field[]>([
    {
      postname: 'ELV_type',
      label: '',
      type: 'hidden',
      value: 'iban',
      is_required: false,
    },
    {
      postname: 'ELV_account_holder',
      label: $localize`:@@payment_method.label.account_holder:Kontoinhaber`,
      placeholder: $localize`:@@payment_method.label.account_holder:Kontoinhaber`,
      type: 'text',
      is_required: true,
    },
    {
      postname: 'ELV_iban',
      label: $localize`:@@payment_method.label.elv_iban:IBAN`,
      placeholder: $localize`:@@payment_method.label.elv_iban:IBAN`,
      type: 'text',
      is_required: true,
      in_row_with_next: true,
      asyncValidators: [async (c: AbstractControl) => await this._ibanCountryValidator(c)],
    },
    {
      postname: 'ELV_bic',
      label: $localize`:@@payment_method.label.elv_bic:BIC`,
      placeholder: $localize`:@@payment_method.label.elv_bic:BIC`,
      type: 'text',
      is_required: false,
      in_row_with_next: true,
      validators: [PaymentValidators.bic],
    },
  ]);

  public legacyFieldList = [
    {
      postname: 'ELV_type',
      label: '',
      type: 'hidden',
      value: 'legacy',
      is_required: false,
    },
    {
      postname: 'ELV_account_holder',
      label: $localize`:@@payment_method.label.account_holder:Kontoinhaber`,
      placeholder: $localize`:@@payment_method.label.account_holder:Kontoinhaber`,
      type: 'text',
      is_required: true,
    },
    {
      postname: 'ELV_account_number',
      label: $localize`:@@payment_method.label.account_number:Kontonummer`,
      placeholder: $localize`:@@payment_method.label.account_number:Kontonummer`,
      type: 'text',
      is_required: true,
      in_row_with_next: true,
      validators: [PaymentValidators.accountNumber],
    },
    {
      postname: 'ELV_routing_number',
      label: $localize`:@@payment_method.label.routing_number_short:BLZ`,
      placeholder: $localize`:@@payment_method.label.routing_number:Bankleitzahl`,
      type: 'text',
      is_required: true,
      // in_row_with_next: true,
      validators: [PaymentValidators.routingNumber],
    },
  ];

  private _formWasChangedManually = new Subject<void>();

  private _copyNameOfAddressToAccountHolder$ = this._orderformFascade.order$.pipe(
    /**
     * Subscribe until user has changed elv form or
     * component was destroyed
     */
    takeUntil(this._formWasChangedManually),
    takeUntilDestroyed(),
    map((orderData) => {
      // dont crash anything
      try {
        // Use company if exists else (first lastname)
        if (orderData.order.shipping_address.buyer_address_company) {
          return orderData.order.shipping_address.buyer_address_company;
        }

        return [
          orderData.order.shipping_address.buyer_address_first_name,
          orderData.order.shipping_address.buyer_address_last_name,
        ]
          .join(' ')
          .trim();
      } catch (e) {
        return '';
      }
    }),
    // Action only name entered!
    filter((name) => !!name),
    tap((holderName) => (this.cachedData.ELV_account_holder = holderName)),
    debounceTime(CopyNameOfAddressToAccountHolderDelay),
    tap((holderName) => {
      // Update preselected value
      const ibanFieldList = this.ibanFieldList$.value;

      ibanFieldList.find((fields) => fields.postname === 'ELV_account_holder').value = holderName;

      // Update preselected value
      this.legacyFieldList.find((fields) => fields.postname === 'ELV_account_holder').value = holderName;

      /**
       * Add new object to tpl to trigger input change
       * PS: this technique is easier as write two observables for two forms
       */
      this.legacyFieldList = [...this.legacyFieldList];
      this.ibanFieldList$.next([...ibanFieldList]);
      this._cdr.detectChanges();
    })
  );

  public elvTypeForm = new UntypedFormControl('iban');

  public cachedData: CustomFormData = {};
  public dataChange$ = new Subject<CustomFormData>();
  public formInputChange = new EventEmitter<CustomFormData>();

  public onChange: (v: any) => void = (v: any) => {};
  private _bicRequiredChangeSubscription: Subscription;
  private _monitoring = inject(MonitoringService);

  constructor(private _orderformFascade: OrderformFascade, private _cdr: ChangeDetectorRef) {
    this._bicRequiredChangeSubscription = this.dataChange$
      .pipe(
        map((data) => this.cachedData.ELV_iban && !('' + this.cachedData.ELV_iban).toUpperCase().startsWith('DE')),
        distinctUntilChanged()
      )
      .subscribe((bicIsRequired) => {
        const ibanFieldList = [...this.ibanFieldList$.value];
        const bicFieldIndex = ibanFieldList.findIndex((fields) => fields.postname === 'ELV_bic');
        ibanFieldList[bicFieldIndex] = {
          ...ibanFieldList[bicFieldIndex],
          is_required: bicIsRequired,
        };
        this.ibanFieldList$.next(ibanFieldList);
      });

    this.formInputChange.pipe(takeUntilDestroyed()).subscribe((data) => {
      if (this.formFieldComponent.isTouched()) {
        this._formWasChangedManually.next();
      }
      this.cachedData = {
        ...data,
        ELV_type: this.elvTypeForm.value,
      };
      this.dataChange$.next(this.cachedData);
      this.onChange({
        ...this.cachedData,
        valid: this.formFieldComponent.isValid(),
      });
    });
  }

  public get invalid(): boolean {
    return this.formFieldComponent.isValid();
  }

  ngOnInit() {
    this.cachedData = { ...this.config.prefilled };

    /**
     * if BE set ELV_Type
     * so set this in elvTypeForm as default (DS-7809)
     */
    if (this.cachedData.ELV_type) {
      this.elvTypeForm.patchValue(this.cachedData.ELV_type);
    } else {
      this.cachedData = {
        ...this.cachedData,
        // replace invalid value from BE because it is ""
        ELV_type: this.elvTypeForm.value,
      };
    }

    // Start watch for name in address
    this._copyNameOfAddressToAccountHolder$.subscribe();
  }

  onStateChange(status: FormControlStatus) {
    this.dataChange$.next(this.cachedData);
    this.onChange({
      ...this.cachedData,
      valid: this.formFieldComponent?.isValid(),
    });
    (async () => {
      if (this.formFieldComponent && !this.formFieldComponent.isValid()) {
        getInvalidFieldListFromFormGroup(await firstValueFrom(this.formFieldComponent.formGroup$)).forEach((field) => {
          field.errors.forEach((err) => {
            this._monitoring.trackValidationError(field.id + ':' + err, 'frontend', 'ds-orderform-payment-elv');
          });
        });
      }
    })();
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
    this.onChange({
      ...this.cachedData,
      valid: this.formFieldComponent?.isValid(),
    });
  }

  registerOnTouched(fn: any): void {}

  writeValue(obj: any): void {}

  ngOnDestroy() {
    this._bicRequiredChangeSubscription.unsubscribe();
  }

  private async _ibanCountryValidator(c: AbstractControl) {
    const orderData = await firstValueFrom(this._orderformFascade.order$);
    const countryCode = orderData.order.shipping_address.buyer_address_country;
    const hasAddress =
      ADDRESS_KEYS_TO_TRY.findIndex(
        (key) => typeof orderData.order.shipping_address[key] !== 'undefined' && orderData.order.shipping_address[key]
      ) >= 0;
    if (!countryCode || !hasAddress) {
      return null;
    }
    if (countryCode === c.value.slice(0, 2)) {
      return null;
    }
    return {
      custom: $localize`:@@payment_method.elv.iban_country_error:Bitte geben Sie eine IBAN aus dem Land Ihrer Anschrift an. Alternativ wählen Sie eine andere Zahlungsmöglichkeit.`,
    };
  }
}
